import { useState, useEffect, useRef, useCallback } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  Paper,
  useTheme,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import {
  TrendingUp,
  Hotel,
  Payment,
  ReportProblem,
  People,
  Visibility,
  CheckCircle,
  Warning,
  Error,
  Info,
  ArrowForward,
  AddCircleOutline,
  QrCode,
  Security,
  History,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { dashboardAPI } from "../../services/api";
import {
  formatCurrency,
  formatTimeAgo,
  getStatusColor,
} from "../../utils/helpers";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  const fetchDashboardDataSecure = useCallback(async () => {
    if (hasFetched.current || !user) return;
    hasFetched.current = true;

    try {
      setLoading(true);
      const [statsRes, activitiesRes, notificationsRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getRecentActivities(10),
        dashboardAPI.getNotifications(),
      ]);

      setStats(statsRes.data.stats);
      setActivities(activitiesRes.data.activities);
      setNotifications(notificationsRes.data.notifications);
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setError(err.response?.data?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDashboardDataSecure();
  }, [fetchDashboardDataSecure]);

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return `Good Morning, ${user?.name?.split(" ")[0]}! 🌅`;
    if (hour < 17) return `Good Afternoon, ${user?.name?.split(" ")[0]}! ☀️`;
    return `Good Evening, ${user?.name?.split(" ")[0]}! 🌙`;
  };

  const GlassCard = ({ children, sx = {}, ...props }) => (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card"
      sx={{ overflow: "hidden", ...sx }}
      {...props}
    >
      {children}
    </Card>
  );

  const StatWidget = ({ title, value, icon, color, subtitle, delay = 0 }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.5 }}
    >
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          border: `1px solid ${theme.palette[color].main}22`,
          transition: "transform 0.18s",
          "&:hover": { transform: "translateY(-4px)" },
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.paper})`,
        }}
      >
        <Avatar
          sx={{
            bgcolor: `${theme.palette[color].main}`,
            width: 56,
            height: 56,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800} color={theme.palette.text.primary}>
            {value}
          </Typography>
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Paper>
    </motion.div>
  );

  const QuickAction = ({ label, icon, onClick, color = "primary" }) => (
    <Button
      variant="outlined"
      fullWidth
      startIcon={icon}
      onClick={onClick}
      sx={{
        borderRadius: 3,
        py: 1.5,
        justifyContent: "flex-start",
        px: 3,
        borderColor: `${theme.palette[color].main}44`,
        "&:hover": {
          bgcolor: `${theme.palette[color].main}11`,
          borderColor: theme.palette[color].main,
        },
      }}
    >
      {label}
    </Button>
  );

  const renderStudentDashboard = () => {
    if (!stats) return null;

    const paymentChartData = [
      { name: "Paid", value: stats.payments.completed, color: "#4caf50" },
      { name: "Pending", value: stats.payments.pending, color: "#ff9800" },
    ];

    return (
      <Grid container spacing={3}>
        {/* Row 1: Key Stats */}
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Total Spent"
            value={formatCurrency(stats.payments.totalAmount)}
            icon={<Payment />}
            color="primary"
            delay={0.1}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Pending Due"
            value={formatCurrency(stats.payments.pendingAmount)}
            icon={<Warning />}
            color="warning"
            subtitle={`${stats.payments.pending} payments`}
            delay={0.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Open Complaints"
            value={stats.complaints.open}
            icon={<ReportProblem />}
            color="error"
            subtitle="Immediate attention"
            delay={0.3}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Active Visitors"
            value={stats.visitors.active}
            icon={<Visibility />}
            color="info"
            delay={0.4}
          />
        </Grid>

        {/* Row 2: Charts & Quick Actions */}
        <Grid item xs={12} md={8}>
          <GlassCard sx={{ p: 0, height: "100%" }}>
            <Box sx={{ p: 3, borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
              <Typography variant="h6" fontWeight="700">Financial Insights</Typography>
            </Box>
            <Box sx={{ p: 3, height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentChartData}
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={8}
                    dataKey="value"
                  >
                    {paymentChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box display="flex" justifyContent="center" gap={4} mt={-4}>
                {paymentChartData.map(item => (
                  <Box key={item.name} display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: item.color }} />
                    <Typography variant="caption">{item.name} ({item.value})</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </GlassCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <GlassCard sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight="700" gutterBottom>Quick Actions</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>Frequent tasks at your fingertips</Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <QuickAction label="Pay Monthly Rent" icon={<Payment />} onClick={() => navigate("/payments")} color="success" />
              <QuickAction label="Report an Issue" icon={<ReportProblem />} onClick={() => navigate("/complaints")} color="error" />
              {/* Removed visitor and entry-exit quick actions per request */}
              <QuickAction 
                label="WhatsApp Support" 
                icon={<WhatsAppIcon />} 
                onClick={() => window.open('https://wa.me/919844398873', '_blank')} 
                color="success" 
              />
            </Box>
          </GlassCard>
        </Grid>

        {/* Row 3: Stay & Neighbors */}
        <Grid item xs={12}>
          {stats.room && (
            <GlassCard sx={{ p: 4, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
              <Box>
                <Typography variant="caption" color="primary" fontWeight="bold">RESIDENCE DETAILS</Typography>
                <Typography variant="h3" fontWeight="800">Room {stats.room.roomNumber}</Typography>
                <Typography variant="h6" color="text.secondary">{stats.room.building} • {stats.room.roomType}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Roommates</Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {stats.room.beds?.filter(b => b.occupant && b.occupant.email !== user.email).map((bed, idx) => (
                    <Chip
                      key={idx}
                      avatar={<Avatar>{bed.occupant.name.charAt(0)}</Avatar>}
                      label={bed.occupant.name}
                      variant="outlined"
                    />
                  ))}
                  {stats.room.beds?.filter(b => b.occupant && b.occupant.email !== user.email).length === 0 && (
                    <Typography variant="body2" color="text.secondary">No roommates found.</Typography>
                  )}
                </Box>
              </Box>
            </GlassCard>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderAdminDashboard = () => {
    if (!stats) return null;

    const roomData = [
      { name: "Available", value: stats.rooms.availableRooms, color: theme.palette.success.main },
      { name: "Occupied", value: stats.rooms.totalOccupants, color: theme.palette.primary.main },
      { name: "Maintenance", value: stats.rooms.maintenanceRooms, color: theme.palette.error.main },
    ];

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Total Revenue"
            value={formatCurrency(stats.payments.completedAmount)}
            icon={<TrendingUp />}
            color="success"
            subtitle={`${stats.rooms.occupancyRate}% Occupancy`}
            delay={0.1}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Total Students"
            value={stats.users.students}
            icon={<People />}
            color="info"
            subtitle={`${stats.users.totalUsers} Total Staff`}
            delay={0.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Gate Activity"
            value={stats.visitors.activeVisitors}
            icon={<Security />}
            color="primary"
            subtitle={`${stats.visitors.waitingApproval} Waiting Approval`}
            delay={0.3}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Urgent Issues"
            value={stats.complaints.urgentComplaints}
            icon={<ReportProblem />}
            color="error"
            subtitle={`${stats.complaints.openComplaints} Total Open`}
            delay={0.4}
          />
        </Grid>

        {/* Charts Section */}
        <Grid item xs={12} md={8}>
          <GlassCard sx={{ p: 4, height: "100%" }}>
            <Typography variant="h6" fontWeight="700" gutterBottom>Live Room Analytics</Typography>
            <Box sx={{ height: 350, mt: 4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={roomData}>
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={1}/>
                      <stop offset="100%" stopColor={theme.palette.primary.light} stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.05)" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <RechartsTooltip cursor={{ fill: "transparent" }} />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={60}>
                    {roomData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </GlassCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <GlassCard sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight="700" gutterBottom>Operations Center</Typography>
            <Box display="flex" flexDirection="column" gap={3} mt={2}>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight="600">Gate Clearances</Typography>
                  <Typography variant="caption" color="warning.main">Waiting</Typography>
                </Box>
                <Button variant="contained" fullWidth color="warning" onClick={() => navigate("/visitors")}>
                  {stats.visitors.waitingApproval} Requests Pending
                </Button>
              </Box>

              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight="600">Overdue Collections</Typography>
                  <Typography variant="caption" color="error.main">High Risk</Typography>
                </Box>
                <Button variant="contained" fullWidth color="error" onClick={() => navigate("/payments")}>
                  {stats.payments.pendingPayments} Accounts Delinquent
                </Button>
              </Box>

              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight="600">Urgent Maintenance</Typography>
                  <Typography variant="caption" color="info.main">Operational</Typography>
                </Box>
                <Button variant="contained" fullWidth color="info" onClick={() => navigate("/complaints")}>
                  {stats.complaints.urgentComplaints} Critical Tasks
                </Button>
              </Box>
            </Box>
          </GlassCard>
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="70vh" gap={2}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
        <Typography variant="body1" color="text.secondary" fontWeight="500">Initializing Hostly Dashboard...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <AnimatePresence>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Alert severity="error" variant="filled" sx={{ borderRadius: 3, mb: 4 }}>{error}</Alert>
            <Button variant="contained" onClick={() => window.location.reload()}>Retry Connection</Button>
          </motion.div>
        </AnimatePresence>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <div className="container">
      {/* Dynamic Header */}
      <Box 
        component={motion.div}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        sx={{ mb: 6, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 3 }}
      >
        <Box>
          <Typography variant="h3" fontWeight="900" sx={{ letterSpacing: "-1px" }}>
            {getWelcomeMessage()}
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight="400">
            {user?.role === "student" 
              ? "Everything looks great! Here's your hostel overview." 
              : "System operations are steady. Review critical tasks below."}
          </Typography>
        </Box>
        <Box>
           <Chip 
            label={new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} 
            icon={<Info />}
            variant="outlined"
            sx={{ px: 1, py: 2, borderRadius: 2 }}
           />
        </Box>
      </Box>

      {/* Main Stats/Charts */}
      {user?.role === "student" ? renderStudentDashboard() : renderAdminDashboard()}

      </div>

      {/* Row 4: Recent Activities & Notifications */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={7}>
          <GlassCard sx={{ height: "100%" }}>
            <Box sx={{ p: 3, borderBottom: "1px solid rgba(0,0,0,0.05)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight="700">Recent Stream</Typography>
              <Button size="small" endIcon={<ArrowForward />}>View All</Button>
            </Box>
            <List sx={{ p: 0 }}>
              {activities.length > 0 ? (
                activities.slice(0, 6).map((activity, index) => (
                  <ListItem 
                    key={index} 
                    divider={index < 5}
                    sx={{ py: 2, "&:hover": { bgcolor: "rgba(0,0,0,0.02)" } }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: `${getStatusColor(activity.status)}.main`, boxShadow: 2 }}>
                        {activity.type === "payment" ? <Payment /> : activity.type === "complaint" ? <ReportProblem /> : <Visibility />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={activity.title}
                      secondary={
                        <Box component="span" display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">{activity.description}</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.6 }}>{formatTimeAgo(activity.timestamp)}</Typography>
                        </Box>
                      }
                      primaryTypographyProps={{ fontWeight: "600" }}
                    />
                  </ListItem>
                ))
              ) : (
                <Box p={4} textAlign="center">
                  <Typography variant="body1" color="text.secondary">No recent activities found.</Typography>
                </Box>
              )}
            </List>
          </GlassCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <GlassCard sx={{ height: "100%", background: `linear-gradient(135deg, ${theme.palette.primary.main}08, #fff)` }}>
             <Box sx={{ p: 3, borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
              <Typography variant="h6" fontWeight="700">Priority Notifications</Typography>
            </Box>
            <Box p={3}>
              {notifications.length > 0 ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  {notifications.map((notif, idx) => (
                    <Paper 
                      key={idx} 
                      elevation={0}
                      sx={{ 
                        p: 2, 
                        borderRadius: 3, 
                        border: `1px solid ${theme.palette[notif.type]?.light || "#eee"}`,
                        bgcolor: `${theme.palette[notif.type]?.dark}05`,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        cursor: notif.action ? "pointer" : "default",
                        "&:hover": { bgcolor: `${theme.palette[notif.type]?.dark}08` }
                      }}
                      onClick={() => notif.action && navigate(notif.action)}
                    >
                      <Info color={notif.type} sx={{ mt: 0.5 }} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="700" color={`${notif.type}.dark`}>
                          {notif.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {notif.message}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <CheckCircle color="success" sx={{ fontSize: 60, mb: 2, opacity: 0.2 }} />
                  <Typography variant="h6">All Clear!</Typography>
                  <Typography variant="body2" color="text.secondary">No pending alerts for your account.</Typography>
                </Box>
              )}
            </Box>
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
